import { useTranslate } from "@tolgee/react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "#/components/ui/button.tsx";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import { LeverageSelector } from "#/components/ui/leverage-selector.tsx";
import { LeverageSlider } from "#/components/ui/leverage-slider.tsx";
import type { useUpdateLeverage } from "#/hooks/use-trading-actions";
import { cn } from "#/lib/utils";
import { formatLeverage } from "../../lib/formatters.ts";
import { getHyperliquidErrorMessage } from "../../lib/hyperliquid-error.ts";
import type { MarginMode } from "./constants/order-form.constants.ts";

type LeverageDialogProps = {
	coin: string;
	value: number;
	selectedIndex: number;
	leverageMarks: number[];
	maxLeverage: number;
	marginMode: MarginMode;
	supportsCrossMargin: boolean;
	hasAvailableFunds: boolean;
	onChange: (nextIndex: number) => void;
	updateLeverage: ReturnType<typeof useUpdateLeverage>;
	triggerClassName?: string;
};

export function LeverageDialog({
	coin,
	value,
	selectedIndex,
	leverageMarks,
	maxLeverage,
	marginMode,
	supportsCrossMargin,
	hasAvailableFunds,
	onChange,
	updateLeverage,
	triggerClassName,
}: LeverageDialogProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const [pendingIndex, setPendingIndex] = useState(selectedIndex);

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen);
		if (nextOpen) {
			setPendingIndex(selectedIndex);
		}
	}

	async function handleConfirm() {
		const pendingLeverage = leverageMarks[pendingIndex] ?? leverageMarks[0];
		if (pendingLeverage === value) {
			setOpen(false);
			return;
		}

		try {
			await updateLeverage.mutateAsync({
				coin,
				isCross: supportsCrossMargin && marginMode === "Cross",
				leverage: pendingLeverage,
			});
			onChange(pendingIndex);
			setOpen(false);
		} catch {
			// error is tracked in updateLeverage.error
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button
					variant={triggerClassName ? "ghost" : "outline"}
					className={cn("flex-1", triggerClassName)}
				>
					{formatLeverage(value)}
				</Button>
			</DialogTrigger>
			<DialogContent className="w-[min(360px,calc(100vw-2rem))] rounded-[18px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),0_30px_60px_rgba(0,0,0,0.45)]">
				<div className="flex items-center justify-between gap-3">
					<DialogTitle className="text-body2-bold text-trade-text-strong">
						{t("leverage.title")}
					</DialogTitle>
					<DialogCloseButton aria-label="Close leverage dialog" />
				</div>

				<div className="mt-3 grid gap-2 text-label1 text-trade-text-secondary">
					<p>
						{t("leverage.description", {
							coin,
							maxLeverage: formatLeverage(maxLeverage),
						})}
					</p>
					<p>{t("leverage.max-position-warning")}</p>
				</div>

				{hasAvailableFunds ? (
					<>
						<div className="mt-3.5 grid gap-2">
							<LeverageSelector
								marks={leverageMarks}
								selectedIndex={pendingIndex}
								onIndexChange={setPendingIndex}
								formatLabel={formatLeverage}
							/>
							<LeverageSlider
								marks={leverageMarks}
								selectedIndex={pendingIndex}
								onIndexChange={setPendingIndex}
								formatLabel={formatLeverage}
							/>
						</div>

						<div className="mt-5 flex items-center gap-2 rounded-[10px] border border-[var(--color-theme-denotives-yellow)]/20 bg-[var(--color-theme-denotives-yellow)]/10 px-3 py-2.5 text-label2 text-[var(--color-theme-denotives-yellow)] dark:text-[var(--color-theme-denotives-yellow)]">
							<AlertTriangle className="h-4 w-4 shrink-0" />
							<span>{t("leverage.risk-warning")}</span>
						</div>

						{updateLeverage.error && (
							<p className="mt-2 text-label2 text-trade-red">
								{getHyperliquidErrorMessage(
									t,
									updateLeverage.error,
									t("toast.error-leverage-update"),
								)}
							</p>
						)}

						<Button
							disabled={updateLeverage.isPending}
							className="mt-3.5 min-h-11 w-full transition-[filter,transform] hover:brightness-105 disabled:opacity-60"
							onClick={handleConfirm}
						>
							{updateLeverage.isPending ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									{t("actions.updating")}
								</>
							) : (
								t("actions.confirm")
							)}
						</Button>
					</>
				) : (
					<div className="mt-4 rounded-[10px] border border-trade-border-subtle bg-trade-surface-inset p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
						<p className="text-label1 text-trade-text-secondary">
							{t("leverage.needs-funds")}
						</p>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
